/**
 * The one visual of /gst-calculator (T1051): the total split into price excluding GST and GST, next to the formula
 * written with the user's numbers. The bar is aria-hidden; the formula is text and "Show as a table" repeats every
 * number, so nothing depends on the picture or on colour (GST is hatched, the price solid, a card gap between them).
 * Inline SVG without a viewBox: x and width are percentages, so the hatch is never stretched.
 */
import type { Money } from "@stb/core/money"
import type { GstBreakdown } from "@stb/jurisdiction-au"
import { ChartColumn, ChevronDown } from "lucide-react"

import type { Locale } from "@/core/i18n/locale"
import { useLocale } from "@/core/i18n/locale-context"

import { CalcHeading, calcEnter, calcVisualCard } from "./calc-ui"
import { formatAud } from "./calculator"
import { gstCopy, gstShareBp, type GstMode } from "./gst-calculator-model"

const DASH = "–"

/** Basis points as "9.1%" (display only). */
const percent = (bp: number) =>
  `${new Intl.NumberFormat("en-AU", { maximumFractionDigits: 1, minimumFractionDigits: 1 }).format(bp / 100)}%`

interface Step {
  label: string
  /** The sum with the user's numbers, without "=" ("A$110.00 ÷ 11"). */
  expr: string
  /** "≈" when the GST was rounded to the cent. */
  sign: "=" | "≈"
  result: string
}

const show = (m: Money | undefined) => (m ? formatAud(m) : DASH)

/** The two sums behind the result, written with the user's numbers: ÷ 11 to remove GST, × 1.1 to add it. */
export function gstFormulaSteps(
  t: GstBreakdown | null,
  mode: GstMode,
  locale: Locale = "en",
): [Step, Step] {
  const v = gstCopy(locale).visual
  if (mode === "remove") {
    return [
      {
        label: v.remove[0],
        expr: `${show(t?.gross)} ÷ 11`,
        sign: t && t.gst.amount * 11 !== t.gross.amount ? "≈" : "=",
        result: show(t?.gst),
      },
      {
        label: v.remove[1],
        expr: `${show(t?.gross)} − ${show(t?.gst)}`,
        sign: "=",
        result: show(t?.net),
      },
    ]
  }
  return [
    {
      label: v.add[0],
      expr: `${show(t?.net)} × 1.1`,
      sign: t && t.net.amount * 11 !== t.gross.amount * 10 ? "≈" : "=",
      result: show(t?.gross),
    },
    {
      label: v.add[1],
      expr: `${show(t?.gross)} − ${show(t?.net)}`,
      sign: "=",
      result: show(t?.gst),
    },
  ]
}

const pct = (bp: number) => `${(bp / 100).toFixed(2)}%`

export function GstSplitVisual({
  id,
  totals,
  mode,
  animate,
}: {
  id: string
  totals: GstBreakdown | null
  mode: GstMode
  animate: boolean
}) {
  const locale = useLocale()
  const v = gstCopy(locale).visual
  const gstBp = totals ? gstShareBp(totals) : 0
  const netBp = totals && totals.gross.amount > 0 ? 10_000 - gstBp : 0
  const hatch = `${id.replace(/[^a-zA-Z0-9_-]/g, "")}-gst-hatch`
  const steps = gstFormulaSteps(totals, mode, locale)
  const [first, second] = steps
  const rows: [string, string, string, string][] = [
    [
      v.net,
      mode === "remove" ? second.expr : DASH,
      show(totals?.net),
      totals && totals.gross.amount > 0 ? percent(netBp) : DASH,
    ],
    [
      v.gst,
      mode === "remove" ? first.expr : second.expr,
      show(totals?.gst),
      totals && totals.gross.amount > 0 ? percent(gstBp) : DASH,
    ],
    [v.gross, mode === "add" ? first.expr : DASH, show(totals?.gross), totals ? "100%" : DASH],
  ]
  // GST gets a minimum visible width so a tiny share still reads as a segment; the text carries the real share.
  const gstW = totals && totals.gross.amount > 0 ? Math.max(gstBp, 150) : 0
  const netW = totals && totals.gross.amount > 0 ? 10_000 - gstW : 0

  return (
    <figure className={`${calcVisualCard} m-0`} data-testid="calc-visual">
      <figcaption>
        <CalcHeading icon={ChartColumn} tone="primary">
          {v.heading}
        </CalcHeading>
      </figcaption>
      <div className="grid items-start gap-4 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] md:gap-6">
        <div className="grid gap-1.5" aria-hidden="true">
          <div className="text-muted-foreground flex justify-between gap-3 text-sm">
            <span>
              {v.net}{" "}
              <b className="text-foreground font-semibold tabular-nums">
                {totals && totals.gross.amount > 0 ? percent(netBp) : DASH}
              </b>
            </span>
            <span>
              {v.gst}{" "}
              <b className="text-foreground font-semibold tabular-nums">
                {totals && totals.gross.amount > 0 ? percent(gstBp) : DASH}
              </b>
            </span>
          </div>
          <svg width="100%" height="24" className="block h-6 w-full overflow-visible">
            <defs>
              <pattern
                id={hatch}
                width="6"
                height="6"
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(45)"
              >
                <rect width="6" height="6" className="fill-primary" />
                <rect width="2" height="6" className="fill-card opacity-55" />
              </pattern>
            </defs>
            <rect width="100%" height="24" rx="4" className="fill-muted" />
            {netW > 0 && (
              <g key={`${totals?.gross.amount}-${mode}`} className={calcEnter(animate)}>
                <rect
                  width={pct(netW)}
                  height="24"
                  rx="4"
                  className="fill-success/70 stroke-card"
                  strokeWidth="2"
                />
                <rect
                  x={pct(netW)}
                  width={pct(gstW)}
                  height="24"
                  rx="4"
                  fill={`url(#${hatch})`}
                  className="stroke-card"
                  strokeWidth="2"
                />
              </g>
            )}
          </svg>
          <div className="text-muted-foreground flex justify-between gap-3 text-sm tabular-nums">
            <span>{show(totals?.net)}</span>
            <span>{show(totals?.gst)}</span>
          </div>
          <div className="border-border flex justify-between gap-3 border-t border-dashed pt-1.5 text-sm">
            <span>{v.total}</span>
            <b className="tabular-nums">{show(totals?.gross)}</b>
          </div>
        </div>
        <ol className="grid gap-2.5" data-testid="calc-formula">
          {steps.map((s) => (
            <li key={s.label} className="grid min-h-11 gap-0.5">
              <span className="text-muted-foreground text-sm">{s.label}</span>
              <span className="text-base tabular-nums">
                {s.expr} {s.sign} <b className="text-success">{s.result}</b>
              </span>
            </li>
          ))}
        </ol>
      </div>
      <details className="group">
        <summary className="text-primary focus-visible:ring-ring flex min-h-11 w-fit cursor-pointer list-none items-center gap-1.5 rounded-sm text-sm font-medium outline-none focus-visible:ring-2 [&::-webkit-details-marker]:hidden">
          <ChevronDown
            className="size-4 transition-transform group-open:rotate-180 motion-reduce:transition-none"
            aria-hidden="true"
          />
          {v.showTable}
        </summary>
        <div className="border-border mt-1 overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <caption className="sr-only">{v.caption}</caption>
            <thead className="bg-muted">
              <tr>
                {v.columns.map((h, n) => (
                  <th
                    key={h}
                    scope="col"
                    className={`px-3 py-2 font-semibold ${n > 1 ? "text-right" : "text-left"}`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(([part, sum, amount, share], n) => (
                <tr key={part} className="border-border border-t">
                  <th
                    scope="row"
                    className={`px-3 py-2 text-left ${n === 2 ? "font-semibold" : "font-normal"}`}
                  >
                    {part}
                  </th>
                  <td className="px-3 py-2 tabular-nums">{sum}</td>
                  <td
                    className={`px-3 py-2 text-right tabular-nums ${n === 2 ? "font-semibold" : ""}`}
                  >
                    {amount}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{share}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  )
}
